import { coreQueryKey } from '@/api/core/authorization';
import {
  changeAdminUserStatus,
  readAdminUser,
  type AdminUser,
  type AdminUserServiceStatus,
} from '@/api/core/users';
import { commandOwner } from '@/pages/property/presentation/command-owner';
import { useCurrentCommand } from '@/pages/property/presentation/use-current-command';
import { Button, DetailPage, FormField, Textarea } from '@mgrvtechgroup/admin-ds';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { lifecycleStatusLabels, serviceStatusLabels } from './user-presentation';
import type { UserRuntime } from './user-queries';

interface StatusBody {
  command_id: string;
  expected_version: number;
  reason: string;
  service_status: AdminUserServiceStatus;
}

export function UserStatusSection({
  runtime,
  userId,
  canEdit,
  onDirtyChange,
}: {
  runtime: UserRuntime;
  userId: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const scope = `user-status:${userId}`;
  const cmd = useCurrentCommand<AdminUser, StatusBody>({
    runtime,
    scope,
    bits: canEdit ? 4 : 2,
    domain: 'user',
    read: async () => {
      const value = await readAdminUser(runtime.client, userId);
      const attempt = commandOwner(runtime, scope).attempt;
      const accepted = attempt?.accepted ? (attempt.body as StatusBody) : undefined;
      if (
        accepted &&
        (value.service_status !== accepted.service_status ||
          (value.version ?? 0) < accepted.expected_version)
      )
        throw new Error('저장된 상태가 요청과 다릅니다. 현재 상태를 다시 조회해 주세요.');
      return value;
    },
    validate: () => Promise.resolve(),
    write: async (body: StatusBody) => {
      await changeAdminUserStatus(runtime.client, userId, body);
      const generation = runtime.session.getSnapshot().generation;
      await Promise.all(
        ['user-detail', 'users'].map((name) =>
          queryClient.invalidateQueries({
            queryKey: coreQueryKey(
              runtime.apiNamespace,
              generation,
              name,
              name === 'users' ? undefined : userId,
            ),
          }),
        ),
      );
    },
  });
  const current = cmd.current;
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const edits = useRef(0);
  const dirty = canEdit && !!current && (reason.length > 0 || !!cmd.attempt);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const region = useRef<HTMLDivElement>(null);
  const confirmTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const before = edits.current;
    queueMicrotask(() => {
      if (edits.current !== before) return;
      setReason('');
      setConfirm(false);
    });
  }, [current?.version, current?.service_status]);
  useEffect(() => {
    if (confirm) region.current?.focus();
  }, [confirm]);

  const locked = cmd.busy || !!cmd.attempt || !cmd.canWrite || !canEdit;
  const target: AdminUserServiceStatus | null = current
    ? current.service_status === 'ACTIVE'
      ? 'SUSPENDED'
      : 'ACTIVE'
    : null;
  const action = target === 'SUSPENDED' ? '일시정지' : '이용 재개';
  return (
    <DetailPage.Section title="계정 상태">
      {cmd.error && <p role="alert">{cmd.error}</p>}
      {cmd.success && <p role="status">저장 후 현재 상태를 다시 확인했습니다.</p>}
      <Button variant="outline" disabled={cmd.busy} onClick={() => void cmd.refresh()}>
        현재 상태 다시 조회
      </Button>
      {!current && !cmd.error && <p role="status">현재 상태를 조회하고 있습니다.</p>}
      {current && target && (
        <div className="space-y-3">
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="caption text-muted-foreground">서비스 상태</dt>
              <dd>{serviceStatusLabels[current.service_status]}</dd>
            </div>
            <div>
              <dt className="caption text-muted-foreground">계정 상태</dt>
              <dd>{lifecycleStatusLabels[current.lifecycle_status]}</dd>
            </div>
            <div>
              <dt className="caption text-muted-foreground">가입</dt>
              <dd>{current.registration_completed ? '완료' : '미완료'}</dd>
            </div>
          </dl>
          {!canEdit && (
            <p className="caption text-muted-foreground">
              일시정지·재개는 사용자 상태 변경 권한이 있는 관리자만 할 수 있습니다.
            </p>
          )}
          {canEdit && (
            <div className="space-y-2 border-t pt-3">
              <FormField label={`${action} 사유`} htmlFor={`status-reason-${userId}`}>
                <Textarea
                  id={`status-reason-${userId}`}
                  value={reason}
                  disabled={locked}
                  maxLength={500}
                  onChange={(event) => {
                    edits.current++;
                    setReason(event.target.value);
                    setConfirm(false);
                  }}
                />
              </FormField>
              <Button
                ref={confirmTrigger}
                disabled={locked || !current.version || !reason.trim()}
                onClick={() => {
                  setConfirm(true);
                }}
              >
                {action} 확인
              </Button>
            </div>
          )}
          {confirm && (
            <div
              ref={region}
              tabIndex={-1}
              role="region"
              aria-label={`${action} 확인`}
              className="space-y-2 rounded border p-3"
            >
              <p className="break-all">대상 사용자: {userId}</p>
              <p>
                {serviceStatusLabels[current.service_status]} → {serviceStatusLabels[target]} ·
                사유: {reason.trim()}
              </p>
              <Button
                disabled={locked}
                onClick={() =>
                  void cmd.submit({
                    command_id: crypto.randomUUID(),
                    expected_version: current.version ?? 0,
                    reason: reason.trim(),
                    service_status: target,
                  })
                }
              >
                확인하고 {action}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setConfirm(false);
                  confirmTrigger.current?.focus();
                }}
              >
                계속 수정
              </Button>
            </div>
          )}
          {cmd.attempt && !cmd.attempt.accepted && (
            <div className="space-y-2">
              <p role="alert">
                저장 결과를 확인하지 못했습니다. 같은 요청의 결과를 먼저 확인합니다.
              </p>
              <Button disabled={cmd.busy} onClick={() => void cmd.submit()}>
                원래 요청 결과 확인
              </Button>
            </div>
          )}
        </div>
      )}
    </DetailPage.Section>
  );
}
